import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionStore } from '../store/sessionStore';
import {
  buildProfileSectionNudge,
  isProfileNudgeRoute,
  type ProfileSectionNudge,
} from '../utils/profileSectionNudges';

type ActiveProfileSectionNudge = ProfileSectionNudge & {
  dismiss: () => void;
};

function resolveUserId(user: unknown): number | string | null {
  if (!user || typeof user !== 'object') {
    return null;
  }
  const id = (user as Record<string, unknown>).id;
  return typeof id === 'number' || typeof id === 'string' ? id : null;
}

export function useProfileSectionNudge(): ActiveProfileSectionNudge | null {
  const location = useLocation();
  const userId = useSessionStore((state) => resolveUserId(state.currentUser));
  const sessionStatus = useSessionStore((state) => state.status);
  const [nudge, setNudge] = useState<ProfileSectionNudge | null>(null);
  const sessionDismissedRef = useRef(false);
  const dismissGenerationRef = useRef(0);

  useEffect(() => {
    sessionDismissedRef.current = false;
    dismissGenerationRef.current += 1;
  }, [userId]);

  const applyNudgeFromSession = useCallback((generationAtStart: number) => {
    if (sessionDismissedRef.current || generationAtStart !== dismissGenerationRef.current) {
      return;
    }

    const session = useSessionStore.getState();
    setNudge(
      buildProfileSectionNudge(
        session.currentUser as Record<string, unknown> | null,
        session.prompts,
        session.portfolio,
        { hiddenForSession: sessionDismissedRef.current }
      )
    );
  }, []);

  const refreshNudge = useCallback(() => {
    if (!isProfileNudgeRoute(location.pathname)) {
      setNudge(null);
      return;
    }

    const session = useSessionStore.getState();
    if (session.status !== 'ready' || !session.currentUser) {
      setNudge(null);
      return;
    }

    if (sessionDismissedRef.current) {
      setNudge(null);
      return;
    }

    const generationAtStart = dismissGenerationRef.current;

    applyNudgeFromSession(generationAtStart);

    void (async () => {
      await useSessionStore.getState().refreshUser({ force: true });
      applyNudgeFromSession(generationAtStart);
    })();
  }, [applyNudgeFromSession, location.pathname]);

  useEffect(() => {
    if (sessionStatus === 'ready' && userId != null) {
      refreshNudge();
    } else {
      setNudge(null);
    }
  }, [refreshNudge, sessionStatus, userId]);

  useEffect(() => {
    const handleRefresh = () => {
      if (sessionDismissedRef.current) {
        return;
      }
      refreshNudge();
    };

    window.addEventListener('profile-updated', handleRefresh);
    window.addEventListener('portfolio-updated', handleRefresh);
    window.addEventListener('prompts-updated', handleRefresh);
    return () => {
      window.removeEventListener('profile-updated', handleRefresh);
      window.removeEventListener('portfolio-updated', handleRefresh);
      window.removeEventListener('prompts-updated', handleRefresh);
    };
  }, [refreshNudge]);

  const dismiss = useCallback(() => {
    sessionDismissedRef.current = true;
    dismissGenerationRef.current += 1;
    setNudge(null);
  }, []);

  if (!nudge) {
    return null;
  }

  return { ...nudge, dismiss };
}
